// @ts-strict-ignore
import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useState } from 'react';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { InputBlockMedia } from '@shared/components/blocks/lib/inputFile/InputBlockMedia';
import { useFileUpload } from '@shared/components/blocks/lib/inputFile/hooks';
import { MBQuestionInputActionButton } from '@shared/components/blocks/lib/inputQuestion/actionButton';
import { MBQuestionFile } from '@shared/components/blocks/lib/inputQuestion/file';
import { MBQuestionNotes } from '@shared/components/blocks/lib/inputQuestion/notes';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1 } from '@shared/components/typography';
import { useSelector } from '@shared/store';
import { grid } from '@shared/styles';
import { useIsDesktop, useTrigger } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { getPrimaryNodeAttribute, isAuditCheckboxComponent } from '../../utils';
import { PrimaryAuditComponent } from './PrimaryAuditComponent';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowComponentStyles } from './WorkflowComponentStyles';

export function WorkflowAuditComponent(props: WorkflowComponentProps) {
  const anchorName = `--workflow-audit-${props.component.id}`;
  const notesAnchorName = `--workflow-audit-${props.component.id}-notes`;
  const [answerTrigger, onAnswerPress] = useTrigger();
  const [notesTrigger, onNotesPress] = useTrigger();
  const primaryNodeAttribute = getPrimaryNodeAttribute(props.component);
  const initialValue = props.initialData?.[primaryNodeAttribute];
  const [value, setValue] = useState(initialValue);
  const [files, setFiles] = useState(props.initialData?.files);
  const [notes, setNotes] = useState(props.initialData?.notes);
  const isDesktop = useIsDesktop();
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const maxImageWidth = isDesktop ? 300 : viewportWidth / 3;
  const maxImageHeight = (maxImageWidth * 2) / 3;

  const onNotesChange = useCallback(
    (updatedNotes: string) => {
      setNotes(updatedNotes);
      props.onDataChanged(props.component.id, {
        [primaryNodeAttribute]: value,
        files,
        notes: updatedNotes,
      });
    },
    [primaryNodeAttribute, files, value, props],
  );

  const onFileAdded = useCallback(
    file => {
      const newValues = isNil(files) ? [] : [...files];
      newValues.push(file);
      setFiles(newValues);
      props.onDataChanged(props.component.id, {
        [primaryNodeAttribute]: value,
        files: newValues,
        notes,
      });
    },
    [files, props, primaryNodeAttribute, value, notes],
  );

  const onFileRemoved = useCallback(
    file => {
      const newValues = files?.filter(item => {
        return item.url !== file.url;
      });
      setFiles(newValues);
      props.onDataChanged(props.component.id, {
        [primaryNodeAttribute]: value,
        files: newValues,
        notes,
      });
    },
    [files, props, primaryNodeAttribute, value, notes],
  );

  const { showFilePicker, uploading } = useFileUpload({
    onFileAdded: onFileAdded,
    onFileRemoved: onFileRemoved,
  });

  if (props.isReadOnly && isEmpty(value) && isEmpty(files) && isEmpty(notes)) {
    return null;
  }

  return (
    <MBView style={WorkflowComponentStyles.container}>
      <PrimaryAuditComponent
        component={props.component}
        currentValue={value}
        onDataChanged={props.onDataChanged}
        filesRef={files}
        notesRef={notes}
        setValue={setValue}
        answerTrigger={answerTrigger}
        anchorName={anchorName}
        isReadOnly={props.isReadOnly}
      />
      {!isEmpty(files) && (
        <View paddingBottom={1}>
          <View style={{ borderRadius: grid(1), overflow: 'hidden' }}>
            <InputBlockMedia
              files={files}
              maxWidth={maxImageWidth}
              maxHeight={maxImageHeight}
              minWidth={maxImageWidth / 2}
              compactFileView={false}
              editable={!props.isReadOnly}
              onRemoved={onFileRemoved}
            />
          </View>
        </View>
      )}
      {notes?.length > 0 && (
        <MBTouchableOpacity onPress={onNotesPress}>
          <MBView
            style={WorkflowComponentStyles.textarea}
            paddingVertical={2}
            paddingHorizontal={1}
          >
            <MBTextBody1>{notes}</MBTextBody1>
          </MBView>
        </MBTouchableOpacity>
      )}
      {!props.isReadOnly && (
        <View row>
          {!isAuditCheckboxComponent(props.component) && (
            <>
              <MBQuestionInputActionButton
                label={i18n('card.blocks.question.answerButtonLabel')}
                onPress={onAnswerPress}
                icon="pencil"
                anchorName={anchorName}
              />
              <View paddingRight={1} />
            </>
          )}
          <MBQuestionFile
            label={i18n('card.blocks.question.addFile')}
            onPress={showFilePicker}
            uploading={uploading}
          />
          <View paddingRight={1} />
          <MBQuestionNotes
            label={i18n('card.blocks.question.notes')}
            placeholder={i18n('card.blocks.question.notesPlaceholder')}
            value={notes}
            onChange={onNotesChange}
            trigger={notesTrigger}
            isReadOnly={props.isReadOnly}
            anchorName={notesAnchorName}
          />
        </View>
      )}
    </MBView>
  );
}
